import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { STYLE_CONSTANTS } from '../../../constants';
import { useAppDispatch, useAppSelector } from '../../../redux-store/hooks';
import {
  FloorViewer, PlacesViewer, TablesViewer
} from '../../client-info';
import { RestaurantTable } from '../../../components';
import React, { FC } from 'react';
import { DEVICE_TYPE, ITables } from '../../../all-types';
import { getTableList, unlockTable } from '../../../services';




const SCROLL_STYLES = {
  overflowY: 'auto',
  //borderRight: `1px solid black`,
  //borderLeft: `1px solid black`,
  scrollbarWidth: 'thin',
  position: "relative",
  overflowX: "hidden",
}

export interface ItableAssignerProps {
  onTableSelect: (tableName: ITables) => void;
}

const TableAssigner: FC<ItableAssignerProps> = ({ onTableSelect }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const tables = useAppSelector(state => state.clientInfo.data.tableManager.tables);
  const places = useAppSelector(state => state.clientInfo.data.tableManager.places);
  const floors = useAppSelector(state => state.clientInfo.data.tableManager.noOffloors);
  const activeBills = useAppSelector(state => state.billing.activeBills);
  const preloadTables = useAppSelector(state => state.preloadData.tables);
  const userRole = useAppSelector(state => state.authentication.role);
  const userId = useAppSelector(state => state.authentication.userId);
  const isAdmin = userRole === 'ADMIN';


  const STANDARD_HEADER_HEIGHT = useAppSelector(state => state.ui.STANDARD_HEADER_HEIGHT);
  const OCCUPIED_HEIGHT = STANDARD_HEADER_HEIGHT + STYLE_CONSTANTS.SETTING_BOTTOM_BAR_HEIGHT;
  const VIEW_HEIGHT = `calc(100vh - ${OCCUPIED_HEIGHT}px)`
  const [selectedPlace, setSelectedPlace] = React.useState<string>("All");
  const [selectedFloor, setSelectedFloor] = React.useState<number>(0);
  const [isRefreshingTables, setIsRefreshingTables] = React.useState(false);
  const filteredTables = Object.values(tables).filter(t => (selectedPlace !== "All" ? t.place === selectedPlace : true) && (selectedFloor != 0 ? t.floorNo === selectedFloor : true));

  const getUniqueTableName = (t: ITables) => {
    return t.tableName.concat(String(t.tableNo)).concat(String(t.floorNo));
  }

  const isTableFree = (t: ITables) => {
    const uniqueName = getUniqueTableName(t);
    if(activeBills[uniqueName]) {
      return false;
    }
    const liveTable = preloadTables.find(p =>
      p.tableName === t.tableName && p.tableNo === t.tableNo && p.floorNo === t.floorNo
    );
    if (liveTable?.billSession) {
      return false;
    }
    return true;
  };

  const getLiveTableInfo = (t: ITables) => {
    const liveTable = preloadTables.find(p =>
      p.tableName === t.tableName && p.tableNo === t.tableNo && p.floorNo === t.floorNo
    );
    // Treat a lock that the current user owns as "not locked" for display
    // purposes — the user owns it, so the table should look normal to them.
    const rawLock = liveTable?.tableLock ?? null;
    const lock = rawLock?.lockedBy && rawLock.lockedBy !== userId ? rawLock : null;
    return { id: liveTable?._id ?? null, lock };
  };

  const handleForceUnlock = (tableId: string) => {
    dispatch(unlockTable({ tableId, lockedBy: userId, force: true }));
  };

  const handleRefreshTables = async () => {
    try {
      setIsRefreshingTables(true);
      await dispatch(getTableList());
    } finally {
      setIsRefreshingTables(false);
    }
  };

  return (
    <Box display="flex" flexDirection={"column"} flex={1}>
      <Stack flexDirection={"row"} flex={1}>
        <Stack flex={1} height={VIEW_HEIGHT} sx={{
          ...SCROLL_STYLES,
          minWidth: "150px"
        }}>
          <PlacesViewer places={places} selectedPlace={selectedPlace} setSelectedPlace={setSelectedPlace} />
        </Stack>
        <Stack flex={8} height={VIEW_HEIGHT} sx={{ ...SCROLL_STYLES }}>
          <Stack flexDirection={"row"} justifyContent={"center"} gap={2} mb={1}>
            <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%", 
                  backgroundColor: theme.palette.available.main, 
                }}
              />
              <Typography variant="h6">Available</Typography>
            </Stack>
            <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%", 
                  backgroundColor: theme.palette.occupied.main, 
                }}
              />
              <Typography variant="h6">Occupied</Typography>
            </Stack>
            <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.offline.main,
                }}
              />
              <Typography variant="h6">Unavailable</Typography>
            </Stack>
            <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
              <LockIcon sx={{ fontSize: 18, color: '#FF9800' }} />
              <Typography variant="h6">Locked</Typography>
            </Stack>
            <Tooltip title="Refresh tables">
              <span>
                <IconButton
                  size="small"
                  onClick={handleRefreshTables}
                  disabled={isRefreshingTables}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.secondary.main,
                    width: 34,
                    height: 34,
                  }}
                >
                  {isRefreshingTables ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

          </Stack>
          <Stack flex={1} flexDirection={"row"} sx={{ flexWrap: "wrap", alignItems: "center", justifyContent: "center" }} gap={2}>
            {(filteredTables && Object.values(filteredTables).length > 0) && Object.values(filteredTables)?.map(t => {
              const { id: liveId, lock } = getLiveTableInfo(t);
              const tooltipTitle = lock
                ? `Locked by ${lock.lockedByName} · ${lock.deviceType === DEVICE_TYPE.MAIN_SYSTEM ? 'Main System' : 'Tablet'} · ${new Date(lock.lockedAt).toLocaleTimeString()}`
                : '';
              return (
                <Stack key={t.tableId} style={{ cursor: lock ? "not-allowed" : "pointer" }} position="relative" alignItems="center">
                  <Tooltip title={tooltipTitle} arrow disableHoverListener={!lock}>
                    <Box position="relative">
                      <RestaurantTable table={t} onTableSelect={onTableSelect} isFree={isTableFree(t)} />
                      {lock && (
                        <Box sx={{
                          position: 'absolute', top: 4, right: 4,
                          backgroundColor: 'rgba(255, 152, 0, 0.95)',
                          borderRadius: '50%', width: 24, height: 24,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                        }}>
                          <LockIcon sx={{ fontSize: 14, color: '#fff' }} />
                        </Box>
                      )}
                    </Box>
                  </Tooltip>

                  {/* Admin force-unlock button — only shown to admin when table is locked */}
                  {lock && isAdmin && liveId && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<LockOpenIcon />}
                      onClick={(e) => { e.stopPropagation(); handleForceUnlock(liveId); }}
                      sx={{ mt: 0.5, fontSize: 10, py: 0.3, px: 1, minWidth: 0, lineHeight: 1.4 }}
                    >
                      Unlock
                    </Button>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
        <Stack flex={1} height={VIEW_HEIGHT} sx={{
          ...SCROLL_STYLES,
          minWidth: "150px"
        }}>
          <FloorViewer floors={floors} selectedFloor={selectedFloor} setSelectedFloor={setSelectedFloor} />
        </Stack>
      </Stack>
    </Box>
  );
};

export { TableAssigner };
